import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Checkbox as MuiCheckbox,
  CircularProgress,
  FormControlLabel,
  styled,
  Typography,
} from '@mui/material'

import {
  ArrowForwardIosSharp as ArrowForwardIosSharpIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'

import {
  TreeItem,
  TreeView
} from '@mui/lab'

import { useDatasetsContext } from '../DatasetsContext'
import { useDataNavigatorControlsContext } from '../DataNavigatorControlsContext'
import { PolygonSymbol } from '../../../common/primitives'
import { colors } from '../../../utils/colorUtils'

const Checkbox = styled(MuiCheckbox)(({ theme }) => ({
  padding: theme.spacing(0.5),
  '&.Mui-checked .MuiSvgIcon-root': {
    color: colors.awaCharcoal75,
  },
}))

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  color: theme.palette.common.white,
  flexDirection: 'row-reverse',
  minHeight: 0,
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.common.white,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
    fontSize: '0.9rem',
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
  maxHeight: 'calc(100vh - 220px)',
  overflowY: 'auto',
  backgroundColor: colors.awaOffWhite,
  color: theme.palette.text.main,
}))

const StyledTreeView = styled(TreeView)(() => ({
  '& .MuiAccordion-root': { border: '1px solid black' },
  '&:first-of-type': {
    '& .MuiAccordion-root, .MuiAccordionSummary-root': {
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
    },
  },
  '&:last-of-type': {
    '& .MuiAccordion-root, .MuiAccordionDetails-root, .MuiAccordionSummary-root:not(.Mui-expanded)': {
      borderBottomLeftRadius: '4px',
      borderBottomRightRadius: '4px',
    },
  },
}))

const TreeControl = ({
  featureSetName,
  featureSetData,
}) => {

  const {
    data,
    loading,
  } = useDatasetsContext()

  const {
    clickDatasetOnDataPanel,
    clickFeatureOnDataPanel,
    clickFeatureSetOnDataPanel,
    expandedFeatureSet,
    featuresOnMap,
  } = useDataNavigatorControlsContext()

  if (!featureSetData) { return null }

  const filterByNode = (node = {}) => (issue = {}) => issue.model === node.name && issue.modelType === featureSetData.name

  const datasetList = (datasets, featureName, featureId) => (
    datasets.map((el, i) => (
      <TreeItem
        key={i}
        nodeId={el.key}
        label={el.key}
        onClick={e => clickDatasetOnDataPanel(e, featureSetName, featureName, featureId, el.key)}
        sx={{ '& .MuiTreeItem-label': { fontSize: '0.8rem' } }}
      />
    ))
  )

  const renderTreeJira = (node) => {
    const datasetsForFeature = data.filter(filterByNode(node))
    if (datasetsForFeature.length === 0) { return null }

    const { id: featureName, color, name, weight, featureId } = node
    const shownOnMap = featuresOnMap[featureSetName].includes(featureName)

    return (
      <TreeItem
        key={featureName}
        nodeId={featureName}
        onClick={e => e.stopPropagation()}
        label={
          <FormControlLabel
            control={
              <Checkbox
                checked={shownOnMap}
                onClick={() => clickFeatureOnDataPanel(featureSetName, featureName, featureId)}
              />
            }
            onClick={(e) => e.stopPropagation()}
            label={
              <>
                <PolygonSymbol
                  color={color}
                  weight={weight}
                  disabled={!shownOnMap}
                  sx={{ float: 'left', opacity: shownOnMap ? 1 : 0.2 }}
                />
                <Typography
                  sx={{
                    float: 'left',
                    fontSize: '0.8rem',
                    marginTop: '3px',
                    marginLeft: '4px',
                  }}
                >
                  {name}
                </Typography>
              </>
            }
            key={featureName}
          />
        }
      >
        {datasetList(datasetsForFeature, featureName, featureId)}
      </TreeItem>
    )
  }

  const renderTreeAllNodes = (nodeArr, loading) => {
    if (!Array.isArray(nodeArr)) { return null }
    const allNodes = nodeArr.map(
      (childNode) => renderTreeJira(childNode)
    )
    if (allNodes.every(el => el === null)) {
      if (loading) {
        return <span style={{ fontSize: '0.8rem', marginLeft: '30px' }}>
          Retrieving datasets, please wait ... <CircularProgress size={13} />
        </span>
      }
      return <span style={{ fontSize: '0.8rem', marginLeft: '30px' }}>
        No datasets found for the applied filters.
      </span>
    }
    return <>
      {loading &&
        <span style={{ fontSize: '0.8rem', marginLeft: '30px' }}>
          Refreshing datasets ... <CircularProgress size={13} />
        </span>}
      {allNodes}
    </>
  }

  const renderTree = (node) =>
    <MuiAccordion
      disableGutters
      elevation={0}
      square
      expanded={expandedFeatureSet === featureSetName}
      onClick={() => clickFeatureSetOnDataPanel(featureSetName)}
    >
      <AccordionSummary sx={{ backgroundColor: node.color }}>
        {node.name}
      </AccordionSummary>
      <AccordionDetails>
        {renderTreeAllNodes(node.children, loading)}
      </AccordionDetails>
    </MuiAccordion>

  return (
    <StyledTreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpanded={[]}
      defaultExpandIcon={<ChevronRightIcon />}
    >
      {renderTree(featureSetData)}
    </StyledTreeView>
  )
}

export default TreeControl